/**
 * Utility functions for the Projects page.
 */

import { FileText, ClipboardList, LayoutDashboard, LucideIcon } from 'lucide-react';
import type { DocumentCategory } from '@/lib/types';
import { formatRelativeDate } from '@/lib/i18n-utils';

/** Category color scheme */
export interface CategoryColors {
  bg: string;
  text: string;
  light: string;
  border: string;
}

/** Status display info */
export interface StatusDisplay {
  message: string;
  color: string;
}

/** Category to icon mapping */
const CATEGORY_ICONS: Record<DocumentCategory | 'default', LucideIcon> = {
  notes: FileText,
  requirements: ClipboardList,
  architecture: LayoutDashboard,
  default: FileText,
};

/** Category to color mapping */
const CATEGORY_COLORS: Record<DocumentCategory | 'default', CategoryColors> = {
  notes: { bg: 'bg-emerald-600', text: 'text-emerald-600', light: 'bg-emerald-50', border: 'border-emerald-600' },
  requirements: { bg: 'bg-rose-600', text: 'text-rose-600', light: 'bg-rose-50', border: 'border-rose-600' },
  architecture: { bg: 'bg-purple-600', text: 'text-purple-600', light: 'bg-purple-50', border: 'border-purple-600' },
  default: { bg: 'bg-slate-600', text: 'text-slate-600', light: 'bg-slate-50', border: 'border-slate-600' },
};

/** Processing status to display info mapping */
const STATUS_DISPLAY: Record<string, StatusDisplay> = {
  pending: { message: 'Waiting to process...', color: 'text-slate-500' },
  transcribing: { message: 'Transcribing audio...', color: 'text-blue-600' },
  generating: { message: 'Generating documentation...', color: 'text-purple-600' },
  failed: { message: 'Processing failed', color: 'text-red-600' },
  default: { message: 'Processing...', color: 'text-slate-500' },
};

/**
 * Get the icon component for a document category.
 */
export function getCategoryIcon(category: DocumentCategory): LucideIcon {
  return CATEGORY_ICONS[category] || CATEGORY_ICONS.default;
}

/**
 * Get the color scheme for a document category.
 */
export function getCategoryColor(category: DocumentCategory): CategoryColors {
  return CATEGORY_COLORS[category] || CATEGORY_COLORS.default;
}

/** Category to IconContainer variant mapping */
type IconContainerVariant = 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'destructive' | 'indigo' | 'cyan' | 'pink';

const CATEGORY_ICON_VARIANTS: Record<DocumentCategory | 'default', IconContainerVariant> = {
  notes: 'success',       // emerald
  requirements: 'destructive', // rose/red
  architecture: 'secondary',   // purple
  default: 'default',
};

/**
 * Get the IconContainer variant for a document category.
 */
export function getCategoryIconVariant(category: DocumentCategory): IconContainerVariant {
  return CATEGORY_ICON_VARIANTS[category] || CATEGORY_ICON_VARIANTS.default;
}

/**
 * Get display info for a processing status.
 */
export function getStatusDisplay(status: string): StatusDisplay {
  return STATUS_DISPLAY[status] || STATUS_DISPLAY.default;
}

/**
 * Format a date string to a relative or absolute date display.
 */
export function formatDate(dateString: string): string {
  return formatRelativeDate(dateString);
}

/**
 * Strip markdown syntax from text, returning plain text.
 */
export function stripMarkdown(text: string): string {
  return text
    .replace(/^#{1,6}\s+/gm, '')       // headers
    .replace(/\*\*(.+?)\*\*/g, '$1')   // bold
    .replace(/__(.+?)__/g, '$1')        // bold (alt)
    .replace(/\*(.+?)\*/g, '$1')        // italic
    .replace(/_(.+?)_/g, '$1')          // italic (alt)
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1') // links
    .replace(/`([^`]+)`/g, '$1')        // inline code
    .replace(/^>\s+/gm, '')             // blockquotes
    .replace(/^[-*+]\s+/gm, '')         // unordered lists
    .replace(/^\d+\.\s+/gm, '')         // ordered lists
    .replace(/^---+$/gm, '')            // horizontal rules
    .replace(/\n{2,}/g, ' ')            // collapse multiple newlines
    .replace(/\n/g, ' ')                // newlines to spaces
    .trim();
}

/**
 * Get a preview of document content (first 100 chars, stripped of markdown).
 */
export function getContentPreview(content: string, maxLength = 100): string {
  const stripped = stripMarkdown(content);
  return stripped.length > maxLength ? `${stripped.slice(0, maxLength)}...` : stripped;
}

/**
 * Default color scheme for empty states.
 */
export const DEFAULT_COLORS: CategoryColors = {
  bg: 'bg-indigo-600',
  text: 'text-indigo-600',
  light: 'bg-indigo-50',
  border: 'border-indigo-600',
};
